import React, { useState, useEffect, useCallback, useRef } from 'react';
import { nutritionApi } from '../api';
import { WellnessTabBar, WellnessWorkspace } from './WellnessCenter';
import { shiftLocalDateInputValue, toLocalDateInputValue } from '../dateUtils';

interface FoodItem {
  id: number;
  name: string;
  category: string;
  icon: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sugar: number;
  saturated_fat: number;
  unsaturated_fat: number;
  trans_fat: number;
  cholesterol: number;
  sodium: number;
  potassium: number;
  calcium: number;
  iron: number;
  zinc: number;
  magnesium: number;
  phosphorus: number;
  selenium: number;
  vitamin_a: number;
  vitamin_b1: number;
  vitamin_b2: number;
  vitamin_b6: number;
  vitamin_b12: number;
  vitamin_c: number;
  vitamin_d: number;
  vitamin_e: number;
  vitamin_k: number;
  folate: number;
  niacin: number;
  water: number;
  caffeine: number;
  alcohol: number;
  gi_value: number;
  is_custom: boolean;
  aliases?: string;
  pinyin?: string;
  is_favorite?: boolean;
  usage_count?: number;
}

interface MealRecord {
  id: number;
  meal_type: string;
  food_name: string;
  food_item_id: number | null;
  icon: string;
  amount: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sugar: number;
  sodium: number;
  potassium?: number;
  cholesterol: number;
  saturated_fat: number;
  unsaturated_fat?: number;
  trans_fat?: number;
  vitamin_c: number;
  calcium: number;
  iron: number;
  zinc?: number;
  magnesium?: number;
  phosphorus?: number;
  selenium?: number;
  vitamin_a?: number;
  vitamin_b1?: number;
  vitamin_b2?: number;
  vitamin_b6?: number;
  vitamin_b12?: number;
  vitamin_d?: number;
  vitamin_e?: number;
  vitamin_k?: number;
  folate?: number;
  niacin?: number;
  water?: number;
  caffeine?: number;
  alcohol?: number;
  date: string;
  note: string;
  created_at: string;
}

interface DailySummary {
  date: string;
  total_calories: number;
  total_protein: number;
  total_fat: number;
  total_carbs: number;
  total_fiber: number;
  total_sugar: number;
  total_sodium: number;
  total_potassium?: number;
  total_cholesterol: number;
  total_saturated_fat: number;
  total_unsaturated_fat?: number;
  total_trans_fat?: number;
  total_vitamin_c: number;
  total_calcium: number;
  total_iron: number;
  total_zinc?: number;
  total_magnesium?: number;
  total_phosphorus?: number;
  total_selenium?: number;
  total_vitamin_a?: number;
  total_vitamin_b1?: number;
  total_vitamin_b2?: number;
  total_vitamin_b6?: number;
  total_vitamin_b12?: number;
  total_vitamin_d?: number;
  total_vitamin_e?: number;
  total_vitamin_k?: number;
  total_folate?: number;
  total_niacin?: number;
  total_water?: number;
  total_caffeine?: number;
  total_alcohol?: number;
  meal_count: number;
  target_calories: number;
  target_protein: number;
  target_fat: number;
  target_carbs: number;
  water_cups: number;
}

interface NutritionGoal {
  // 宏量营养素
  target_calories: number;
  target_protein: number;
  target_fat: number;
  target_carbs: number;
  target_fiber: number;
  target_sugar: number;
  // 脂肪细分
  target_saturated_fat: number;
  target_cholesterol: number;
  // 矿物质
  target_sodium: number;
  target_potassium: number;
  target_calcium: number;
  target_iron: number;
  target_zinc: number;
  target_magnesium: number;
  target_phosphorus: number;
  target_selenium: number;
  // 维生素
  target_vitamin_a: number;
  target_vitamin_b1: number;
  target_vitamin_b2: number;
  target_vitamin_b6: number;
  target_vitamin_b12: number;
  target_vitamin_c: number;
  target_vitamin_d: number;
  target_vitamin_e: number;
  target_vitamin_k: number;
  target_folate: number;
  target_niacin: number;
  // 其他
  target_water_cups: number;
}

// 每日推荐摄入量参考 (基于中国DRIs 2023)
const DRI_REFERENCE: Record<string, { label: string; dri: string; source: string }> = {
  target_calories: { label: '热量', dri: '2000-2400 kcal', source: 'EER' },
  target_protein: { label: '蛋白质', dri: '男65g / 女55g', source: 'RNI' },
  target_fat: { label: '脂肪', dri: '总能量20-30%', source: 'AMDR' },
  target_carbs: { label: '碳水化合物', dri: '总能量50-65%', source: 'AMDR' },
  target_fiber: { label: '膳食纤维', dri: '25-30g', source: 'AI' },
  target_sugar: { label: '添加糖', dri: '<50g (最好<25g)', source: 'WHO' },
  target_saturated_fat: { label: '饱和脂肪', dri: '<总能量10%', source: 'UL' },
  target_cholesterol: { label: '胆固醇', dri: '<300mg', source: '建议' },
  target_sodium: { label: '钠', dri: '<2000mg (≈5g盐)', source: 'WHO/PI' },
  target_potassium: { label: '钾', dri: '2000mg', source: 'AI' },
  target_calcium: { label: '钙', dri: '800mg (50+岁1000mg)', source: 'RNI' },
  target_iron: { label: '铁', dri: '男12mg / 女20mg', source: 'RNI' },
  target_zinc: { label: '锌', dri: '男12.5mg / 女7.5mg', source: 'RNI' },
  target_magnesium: { label: '镁', dri: '330mg', source: 'RNI' },
  target_phosphorus: { label: '磷', dri: '720mg', source: 'RNI' },
  target_selenium: { label: '硒', dri: '60μg', source: 'RNI' },
  target_vitamin_a: { label: '维生素A', dri: '男800 / 女700 μg RAE', source: 'RNI' },
  target_vitamin_b1: { label: '维生素B1', dri: '男1.4 / 女1.2 mg', source: 'RNI' },
  target_vitamin_b2: { label: '维生素B2', dri: '男1.4 / 女1.2 mg', source: 'RNI' },
  target_vitamin_b6: { label: '维生素B6', dri: '1.4mg', source: 'AI' },
  target_vitamin_b12: { label: '维生素B12', dri: '2.4μg', source: 'RNI' },
  target_vitamin_c: { label: '维生素C', dri: '100mg', source: 'RNI' },
  target_vitamin_d: { label: '维生素D', dri: '10μg (65+岁15μg)', source: 'RNI' },
  target_vitamin_e: { label: '维生素E', dri: '14mg', source: 'AI' },
  target_vitamin_k: { label: '维生素K', dri: '80μg', source: 'AI' },
  target_folate: { label: '叶酸', dri: '400μg DFE', source: 'RNI' },
  target_niacin: { label: '烟酸(B3)', dri: '男15 / 女12 mg', source: 'RNI' },
};

interface GroupedMeals {
  [key: string]: {
    label: string;
    meals: MealRecord[];
    total_calories: number;
  };
}

type ReviewPeriod = 'recent' | 'week' | 'month';

interface ReviewDay {
  date: string;
  total_calories: number;
  total_protein: number;
  total_fat: number;
  total_carbs: number;
  meal_count: number;
  water_cups: number;
}

interface NutritionReview {
  period: ReviewPeriod;
  label: string;
  start_date: string;
  end_date: string;
  recorded_days: number;
  days: ReviewDay[];
  totals: DailySummary;
  averages: Partial<DailySummary>;
  top_foods: Array<{
    food_name: string;
    icon: string;
    meal_count: number;
    total_calories: number;
    total_amount: number;
  }>;
  meals: MealRecord[];
}

interface QuickMealOption {
  date?: string;
  meal_type: string;
  label?: string;
  name?: string;
  uses?: number;
  items: MealRecord[];
}

interface QuickOptions {
  recent_foods: FoodItem[];
  favorite_foods: FoodItem[];
  frequent_combos: QuickMealOption[];
  last_meal: QuickMealOption | null;
}

interface MealCartItem {
  food: FoodItem;
  amount: number;
}

interface Props {
  onClose: () => void;
  onNavigateWorkspace?: (w: WellnessWorkspace) => void;
  onHpUpdated?: (hp: { before: number; after: number; delta: number; details: Record<string, unknown> }) => void;
  /** From home or records: start a meal, focus water, or open the recent review. */
  initialAction?: 'add_meal' | 'focus_water' | 'review';
  initialDate?: string;
}

const MEAL_TYPES = [
  { key: 'breakfast', label: '早餐', icon: '🌅', color: '#f59e0b' },
  { key: 'lunch', label: '午餐', icon: '☀️', color: '#10b981' },
  { key: 'dinner', label: '晚餐', icon: '🌙', color: '#6366f1' },
  { key: 'snack', label: '加餐', icon: '🍪', color: '#ec4899' },
];

const FOOD_CATEGORIES = [
  { key: '', label: '全部', icon: '📋' },
  { key: 'staple', label: '主食', icon: '🍚' },
  { key: 'meat', label: '肉类', icon: '🥩' },
  { key: 'seafood', label: '海鲜', icon: '🐟' },
  { key: 'vegetable', label: '蔬菜', icon: '🥦' },
  { key: 'fruit', label: '水果', icon: '🍎' },
  { key: 'dairy', label: '乳制品', icon: '🥛' },
  { key: 'bean', label: '豆类', icon: '🫘' },
  { key: 'nut', label: '坚果', icon: '🥜' },
  { key: 'snack', label: '零食', icon: '🍿' },
  { key: 'beverage', label: '饮品', icon: '☕' },
];

const NutritionManager: React.FC<Props> = ({ onClose, onNavigateWorkspace, onHpUpdated, initialAction, initialDate }) => {
  const waterSectionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const initialActionConsumed = useRef(false);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [goal, setGoal] = useState<NutritionGoal | null>(null);
  const [grouped, setGrouped] = useState<GroupedMeals>({});
  const [selectedDate, setSelectedDate] = useState(() => initialDate || toLocalDateInputValue());
  const [latestRecordedDate, setLatestRecordedDate] = useState<string | null>(null);
  const [hpFeedback, setHpFeedback] = useState('');

  // Add meal modal
  const [showAddMeal, setShowAddMeal] = useState(false);
  const [addMealType, setAddMealType] = useState('breakfast');
  const [foods, setFoods] = useState<FoodItem[]>([]);
  const [foodSearchInput, setFoodSearchInput] = useState('');
  const [foodSearch, setFoodSearch] = useState('');
  const [foodCategory, setFoodCategory] = useState('');
  const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
  const [mealAmount, setMealAmount] = useState('100');
  const [customFoodName, setCustomFoodName] = useState('');
  const [customAliases, setCustomAliases] = useState('');
  const [customCalories, setCustomCalories] = useState('');
  const [saveCustomFood, setSaveCustomFood] = useState(true);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [mealCart, setMealCart] = useState<MealCartItem[]>([]);
  const [quickOptions, setQuickOptions] = useState<QuickOptions | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingMeal, setEditingMeal] = useState<MealRecord | null>(null);
  const [editMealType, setEditMealType] = useState('snack');
  const [editFoodName, setEditFoodName] = useState('');
  const [editAmount, setEditAmount] = useState('100');
  const [editCalories, setEditCalories] = useState('0');
  const [editNote, setEditNote] = useState('');

  // Unified nutrition review: recent entries, weekly recap and monthly recap.
  const [showReview, setShowReview] = useState(false);
  const [reviewPeriod, setReviewPeriod] = useState<ReviewPeriod>('recent');
  const [reviewData, setReviewData] = useState<NutritionReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyMealType, setHistoryMealType] = useState('');

  // Expand meal item to show detailed micronutrients
  const [expandedMealId, setExpandedMealId] = useState<number | null>(null);

  // Collapsible micronutrient sections
  const [expandMinerals, setExpandMinerals] = useState(false);
  const [expandVitamins, setExpandVitamins] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, mealsRes] = await Promise.all([
        nutritionApi.getDailySummary(selectedDate),
        nutritionApi.getMeals(selectedDate),
      ]);
      setSummary(summaryRes.data.summary);
      setGoal(summaryRes.data.goal);
      setGrouped(mealsRes.data.grouped || {});
    } catch (err) {
      console.error('加载营养数据失败:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadHistoryMeta = useCallback(async () => {
    try {
      const res = await nutritionApi.getHistoryMeta();
      setLatestRecordedDate(res.data.latest_recorded_date || null);
    } catch (err) {
      console.error('加载饮食历史日期失败:', err);
    }
  }, []);

  useEffect(() => { loadHistoryMeta(); }, [loadHistoryMeta]);

  useEffect(() => {
    if (loading || !initialAction || initialActionConsumed.current) return;
    if (initialAction === 'add_meal') {
      initialActionConsumed.current = true;
      const h = new Date().getHours();
      if (h < 10) setAddMealType('breakfast');
      else if (h < 14) setAddMealType('lunch');
      else if (h < 18) setAddMealType('snack');
      else setAddMealType('dinner');
      setShowAddMeal(true);
    }
    if (initialAction === 'focus_water') {
      const t = window.setTimeout(() => {
        initialActionConsumed.current = true;
        waterSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
      return () => clearTimeout(t);
    }
    if (initialAction === 'review') {
      initialActionConsumed.current = true;
      setReviewPeriod('recent');
      setShowReview(true);
    }
  }, [loading, initialAction]);

  const loadFoods = useCallback(async () => {
    try {
      const res = await nutritionApi.getFoods({ category: foodCategory, search: foodSearch, limit: 20 });
      setFoods(res.data || []);
    } catch (err) {
      console.error('加载食物库失败:', err);
    }
  }, [foodCategory, foodSearch]);

  const loadQuickOptions = useCallback(async () => {
    try {
      const res = await nutritionApi.getQuickOptions();
      setQuickOptions(res.data);
    } catch (err) {
      console.error('加载快捷饮食选项失败:', err);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setFoodSearch(foodSearchInput.trim()), 320);
    return () => clearTimeout(t);
  }, [foodSearchInput]);

  useEffect(() => {
    if (showAddMeal) {
      loadFoods();
      loadQuickOptions();
      window.setTimeout(() => searchInputRef.current?.focus(), 80);
    }
  }, [showAddMeal, loadFoods, loadQuickOptions]);

  useEffect(() => {
    if (!showAddMeal && !editingMeal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (editingMeal) setEditingMeal(null);
      else setShowAddMeal(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showAddMeal, editingMeal]);

  useEffect(() => {
    if (!showAddMeal) {
      setFoodSearchInput('');
      setFoodSearch('');
      setSelectedFood(null);
      setMealAmount('100');
    }
  }, [showAddMeal]);

  const loadReview = useCallback(async () => {
    try {
      setReviewLoading(true);
      const res = await nutritionApi.getReview(reviewPeriod, selectedDate);
      setReviewData(res.data);
    } catch (err) {
      console.error('加载饮食回顾失败:', err);
      setReviewData(null);
    } finally {
      setReviewLoading(false);
    }
  }, [reviewPeriod, selectedDate]);

  useEffect(() => {
    if (showReview) loadReview();
  }, [showReview, loadReview]);

  const applyHpFeedback = useCallback((hp: any) => {
    if (!hp) return;
    onHpUpdated?.(hp);
    setHpFeedback(hp.delta > 0
      ? `饮食记录完成，HP +${hp.delta}（当前 ${hp.after}）`
      : hp.delta < 0
        ? `饮食记录已更新，HP ${hp.delta}（当前 ${hp.after}）`
        : `饮食记录已更新，当前 HP ${hp.after}`);
    window.setTimeout(() => setHpFeedback(''), 3600);
  }, [onHpUpdated]);

  const selectFoodForCart = (food: FoodItem) => {
    const existing = mealCart.find(item => item.food.id === food.id);
    setSelectedFood(food);
    setMealAmount(String(existing?.amount || 100));
    if (!existing) setMealCart(items => [...items, { food, amount: 100 }]);
  };

  const updateCartAmount = (foodId: number, amount: number) => {
    if (!Number.isFinite(amount) || amount <= 0) return;
    setMealCart(items => items.map(item => item.food.id === foodId ? { ...item, amount } : item));
    if (selectedFood?.id === foodId) setMealAmount(String(amount));
  };

  const removeCartFood = (foodId: number) => {
    setMealCart(items => items.filter(item => item.food.id !== foodId));
    if (selectedFood?.id === foodId) setSelectedFood(null);
  };

  const handleToggleFavorite = async (food: FoodItem) => {
    try {
      const favorite = !food.is_favorite;
      await nutritionApi.setFoodFavorite(food.id, favorite);
      setFoods(items => items.map(item => item.id === food.id ? { ...item, is_favorite: favorite } : item));
      setMealCart(items => items.map(item => item.food.id === food.id ? { ...item, food: { ...item.food, is_favorite: favorite } } : item));
      await loadQuickOptions();
    } catch (err: any) {
      alert(err.response?.data?.detail || '收藏操作失败');
    }
  };

  const handleCopyMealIds = async (recordIds: number[], mealType?: string, targetDate = selectedDate) => {
    if (!recordIds.length || submitting) return;
    try {
      setSubmitting(true);
      const res = await nutritionApi.copyMeals({ record_ids: recordIds, date: targetDate, meal_type: mealType });
      applyHpFeedback(res.data.hp);
      setShowAddMeal(false);
      setMealCart([]);
      await Promise.all([loadData(), loadHistoryMeta(), loadQuickOptions(), showReview ? loadReview() : Promise.resolve()]);
    } catch (err: any) {
      alert(err.response?.data?.detail || '复制饮食失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMeal = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const items: any[] = mealCart.map(item => ({
        food_name: item.food.name,
        food_item_id: item.food.id,
        amount: item.amount,
      }));
      if (customFoodName.trim()) {
        const amount = Number(mealAmount) || 100;
        const calories = Number(customCalories) || 0;
        if (saveCustomFood) {
          const foodRes = await nutritionApi.createFood({
            name: customFoodName.trim(),
            aliases: customAliases.trim(),
            pinyin: customAliases.trim().toLowerCase(),
            calories: Math.round((calories * 100 / amount) * 10) / 10,
          });
          items.push({ food_name: foodRes.data.name, food_item_id: foodRes.data.id, amount });
        } else {
          items.push({ food_name: customFoodName.trim(), amount, calories });
        }
      }
      if (!items.length) throw new Error('请选择至少一种食物');

      const res = await nutritionApi.createMealsBatch({
        meal_type: addMealType,
        date: selectedDate,
        items,
      });
      applyHpFeedback(res.data.hp);
      setShowAddMeal(false);
      setSelectedFood(null);
      setMealCart([]);
      setCustomFoodName('');
      setCustomAliases('');
      setCustomCalories('');
      setMealAmount('100');
      setShowCustomInput(false);
      await Promise.all([loadData(), loadHistoryMeta(), loadQuickOptions()]);
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || '添加失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMeal = async (recordId: number) => {
    if (!window.confirm('确定删除此记录？')) return;
    try {
      const res = await nutritionApi.deleteMeal(recordId);
      applyHpFeedback(res.data.hp);
      await Promise.all([loadData(), loadHistoryMeta()]);
    } catch (err: any) {
      alert(err.response?.data?.detail || '删除失败');
    }
  };

  const openEditMeal = (meal: MealRecord) => {
    setEditingMeal(meal);
    setEditMealType(meal.meal_type);
    setEditFoodName(meal.food_name);
    setEditAmount(String(meal.amount));
    setEditCalories(String(Math.round(meal.calories * 10) / 10));
    setEditNote(meal.note || '');
  };

  const handleUpdateMeal = async () => {
    if (!editingMeal || submitting) return;
    try {
      setSubmitting(true);
      const res = await nutritionApi.updateMeal(editingMeal.id, {
        meal_type: editMealType,
        food_name: editFoodName.trim(),
        amount: Number(editAmount),
        calories: Number(editCalories),
        note: editNote.trim(),
      });
      applyHpFeedback(res.data.hp);
      setEditingMeal(null);
      await Promise.all([loadData(), loadHistoryMeta()]);
    } catch (err: any) {
      alert(err.response?.data?.detail || '编辑失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWater = async () => {
    try {
      const res = await nutritionApi.addWater({ date: selectedDate });
      if (summary) {
        setSummary({ ...summary, water_cups: res.data.water_cups });
      }
    } catch (err) {
      console.error('记录饮水失败:', err);
    }
  };

  const handleDateChange = (offset: number) => {
    setSelectedDate(shiftLocalDateInputValue(selectedDate, offset));
  };

  const today = toLocalDateInputValue();
  const isToday = selectedDate === today;
  const filteredReviewMeals = (reviewData?.meals || []).filter(meal => {
    const matchesType = !historyMealType || meal.meal_type === historyMealType;
    const query = historySearch.trim().toLowerCase();
    const matchesSearch = !query || meal.food_name.toLowerCase().includes(query) || (meal.note || '').toLowerCase().includes(query);
    return matchesType && matchesSearch;
  });

  const calPct = summary && goal ? Math.min(100, Math.round((summary.total_calories / goal.target_calories) * 100)) : 0;
  const proteinPct = summary && goal ? Math.min(100, Math.round((summary.total_protein / goal.target_protein) * 100)) : 0;
  const fatPct = summary && goal ? Math.min(100, Math.round((summary.total_fat / goal.target_fat) * 100)) : 0;
  const carbsPct = summary && goal ? Math.min(100, Math.round((summary.total_carbs / goal.target_carbs) * 100)) : 0;
  const waterPct = summary && goal ? Math.min(100, Math.round((summary.water_cups / goal.target_water_cups) * 100)) : 0;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    return `${d.getMonth() + 1}月${d.getDate()}日 周${weekdays[d.getDay()]}`;
  };

  return (
    <div className="nt-root">
      {/* Header */}
      <header className="nt-header">
        <div className="nt-header-top">
          <button className="nt-back-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <h1>🥗 营养管理</h1>
          <button className="nt-week-btn" onClick={() => setShowAddMeal(true)} aria-label="添加饮食记录">
            <span>＋ 记录</span>
          </button>
        </div>

      </header>

      {onNavigateWorkspace && (
        <WellnessTabBar active="nutrition" onNavigate={(w) => w !== 'nutrition' && onNavigateWorkspace(w)} />
      )}

      <div className="nt-view-tabs" role="tablist" aria-label="营养管理视图">
        <button type="button" role="tab" aria-selected={!showReview} className={!showReview ? 'active' : ''} onClick={() => setShowReview(false)}>今天与记录</button>
        <button type="button" role="tab" aria-selected={showReview} className={showReview ? 'active' : ''} onClick={() => setShowReview(true)}>历史与分析</button>
      </div>

      <div className="nt-date-toolbar" aria-label="选择营养记录日期">
        <button className="nt-date-arrow" onClick={() => handleDateChange(-1)} aria-label="前一天">‹</button>
        <label className="nt-date-picker">
          <span className="nt-date-text">{isToday ? '今天' : formatDate(selectedDate)}</span>
          <input type="date" max={today} value={selectedDate} onChange={event => setSelectedDate(event.target.value || today)} aria-label="选择日期" />
        </label>
        <button className="nt-date-arrow" onClick={() => handleDateChange(1)} disabled={isToday} aria-label="后一天">›</button>
        {!isToday && <button className="nt-date-shortcut" type="button" onClick={() => setSelectedDate(today)}>今天</button>}
        {latestRecordedDate && latestRecordedDate !== selectedDate && (
          <button className="nt-date-shortcut" type="button" onClick={() => setSelectedDate(latestRecordedDate)}>最近记录</button>
        )}
      </div>

      {hpFeedback && <div className="nt-hp-feedback" role="status">✨ {hpFeedback}</div>}

      <main className="nt-main">
        {loading ? (
          <div className="nt-loading"><div className="nt-spinner" /><p>加载中...</p></div>
        ) : showReview ? (
          <div className="nt-review">
            <div className="nt-review-heading">
              <div>
                <h3 className="nt-section-title">🍽️ 饮食回顾</h3>
                <p>集中查看近期记录，并按周、月复盘营养摄入。</p>
              </div>
              {reviewData && <span className="nt-review-range">{reviewData.start_date.slice(5).replace('-', '/')} - {reviewData.end_date.slice(5).replace('-', '/')}</span>}
            </div>
            <div className="nt-review-tabs" role="tablist" aria-label="饮食回顾周期">
              {([['recent', '近期'], ['week', '本周'], ['month', '本月']] as Array<[ReviewPeriod, string]>).map(([period, label]) => (
                <button key={period} type="button" role="tab" aria-selected={reviewPeriod === period}
                  className={reviewPeriod === period ? 'active' : ''} onClick={() => setReviewPeriod(period)}>
                  {label}
                </button>
              ))}
            </div>

            {reviewLoading ? <div className="nt-loading"><div className="nt-spinner" /><p>加载饮食回顾...</p></div> : reviewData && (() => {
              const maxCalories = Math.max(...reviewData.days.map(day => day.total_calories || 0), 1);
              const avg = reviewData.averages;
              const listTitle = reviewPeriod === 'recent' ? '近期饮食记录' : `${reviewData.label}饮食清单`;
              return <>
                <div className="nt-review-hero">
                  <span>{reviewData.label}已记录</span>
                  <strong>{reviewData.recorded_days}</strong>
                  <small>天 · 共 {reviewData.totals.meal_count || 0} 餐</small>
                </div>
                <div className="nt-review-stats">
                  <div><span>记录日均热量</span><strong>{Math.round(avg.total_calories || 0)} kcal</strong></div>
                  <div><span>日均蛋白质</span><strong>{Math.round((avg.total_protein || 0) * 10) / 10} g</strong></div>
                  <div><span>日均碳水</span><strong>{Math.round((avg.total_carbs || 0) * 10) / 10} g</strong></div>
                  <div><span>日均饮水</span><strong>{Math.round((avg.water_cups || 0) * 10) / 10} 杯</strong></div>
                </div>
                <section className="nt-review-card">
                  <div className="nt-review-card-title"><h4>热量趋势</h4><span>kcal</span></div>
                  <div className="nt-review-chart">
                    {reviewData.days.map((day) => {
                      const dayDate = new Date(`${day.date}T00:00:00`);
                      const height = day.meal_count ? Math.max(5, (day.total_calories / maxCalories) * 100) : 0;
                      return <button type="button" key={day.date} className="nt-review-bar-wrap" onClick={() => { setSelectedDate(day.date); setShowReview(false); }} aria-label={`查看${day.date}饮食记录`}>
                        <span>{day.meal_count ? Math.round(day.total_calories) : ''}</span>
                        <i><b style={{ height: `${height}%` }} /></i>
                        <em>{dayDate.getMonth() + 1}/{dayDate.getDate()}</em>
                      </button>;
                    })}
                  </div>
                  <p className="nt-review-hint">点击任一天可查看当日完整饮食记录。</p>
                </section>
                <section className="nt-review-card">
                  <div className="nt-review-card-title"><h4>常吃食物</h4><span>按热量排序</span></div>
                  {reviewData.top_foods.length ? <div className="nt-review-foods">
                    {reviewData.top_foods.map(food => <div key={`${food.food_name}-${food.icon}`}><span>{food.icon}</span><b>{food.food_name}</b><small>{food.meal_count} 次 · {Math.round(food.total_calories)} kcal</small></div>)}
                  </div> : <p className="nt-review-empty">该周期还没有饮食记录。</p>}
                </section>
                <section className="nt-review-card">
                  <div className="nt-review-card-title"><h4>{listTitle}</h4><span>{filteredReviewMeals.length} / {reviewData.meals.length} 条</span></div>
                  <div className="nt-history-filters">
                    <input type="search" value={historySearch} onChange={event => setHistorySearch(event.target.value)} placeholder="搜索食物或备注" aria-label="搜索历史饮食" />
                    <select value={historyMealType} onChange={event => setHistoryMealType(event.target.value)} aria-label="按餐次筛选">
                      <option value="">全部餐次</option>
                      {MEAL_TYPES.map(type => <option value={type.key} key={type.key}>{type.label}</option>)}
                    </select>
                  </div>
                  {filteredReviewMeals.length ? <div className="nt-review-meals">
                    {filteredReviewMeals.map(meal => <div className="nt-history-meal-row" key={meal.id}>
                      <button type="button" className="nt-history-open" onClick={() => { setSelectedDate(meal.date); setShowReview(false); }}>
                        <span className="nt-review-meal-icon">{meal.icon}</span><span className="nt-review-meal-name">{meal.food_name}<small>{formatDate(meal.date)} · {MEAL_TYPES.find(type => type.key === meal.meal_type)?.label || '加餐'}</small></span><strong>{Math.round(meal.calories)}<small> kcal</small></strong>
                      </button>
                      <button type="button" className="nt-history-edit" onClick={() => openEditMeal(meal)}>编辑</button>
                      <button type="button" className="nt-history-copy" onClick={() => handleCopyMealIds([meal.id], undefined, today)}>复制到今天</button>
                    </div>)}
                  </div> : <p className="nt-review-empty">先记录一餐，回顾会自动汇总在这里。</p>}
                </section>
              </>;
            })()}
          </div>
        ) : (
          <>
            {/* Calorie Ring */}
            <div className="nt-cal-card">
              <div className="nt-cal-ring-wrap">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(30,41,59,0.8)" strokeWidth="8" />
                  <circle cx="60" cy="60" r="50" fill="none" stroke={calPct >= 100 ? '#f43f5e' : '#10b981'} strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${2 * Math.PI * 50 * (1 - calPct / 100)}`}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '60px 60px', transition: 'stroke-dashoffset 0.6s ease' }}
                  />
                </svg>
                <div className="nt-cal-ring-text">
                  <span className="nt-cal-num">{Math.round(summary?.total_calories || 0)}</span>
                  <span className="nt-cal-unit">kcal</span>
                </div>
              </div>
              <div className="nt-cal-info">
                <div className="nt-cal-target">目标 {goal?.target_calories || 2000} kcal</div>
                <div className="nt-cal-remain">
                  {(goal?.target_calories || 2000) - (summary?.total_calories || 0) > 0
                    ? `还可摄入 ${Math.round((goal?.target_calories || 2000) - (summary?.total_calories || 0))} kcal`
                    : `已超出 ${Math.round((summary?.total_calories || 0) - (goal?.target_calories || 2000))} kcal`
                  }
                </div>
              </div>
            </div>

            {/* Macro Nutrients */}
            <div className="nt-macros">
              <div className="nt-macro">
                <div className="nt-macro-header">
                  <span className="nt-macro-label">蛋白质</span>
                  <span className="nt-macro-val">{Math.round(summary?.total_protein || 0)}g / {goal?.target_protein || 60}g</span>
                </div>
                <div className="nt-macro-bar-bg"><div className="nt-macro-bar" style={{ width: `${proteinPct}%`, background: '#3b82f6' }} /></div>
              </div>
              <div className="nt-macro">
                <div className="nt-macro-header">
                  <span className="nt-macro-label">脂肪</span>
                  <span className="nt-macro-val">{Math.round(summary?.total_fat || 0)}g / {goal?.target_fat || 65}g</span>
                </div>
                <div className="nt-macro-bar-bg"><div className="nt-macro-bar" style={{ width: `${fatPct}%`, background: '#f59e0b' }} /></div>
              </div>
              <div className="nt-macro">
                <div className="nt-macro-header">
                  <span className="nt-macro-label">碳水</span>
                  <span className="nt-macro-val">{Math.round(summary?.total_carbs || 0)}g / {goal?.target_carbs || 300}g</span>
                </div>
                <div className="nt-macro-bar-bg"><div className="nt-macro-bar" style={{ width: `${carbsPct}%`, background: '#10b981' }} /></div>
              </div>
            </div>

            {/* Micronutrients Panel - with DRI recommendations */}
            <div className="nt-micro-card">
              <h4 className="nt-micro-title">🔬 微量营养素 & 每日推荐摄入量</h4>
              
              {/* 常用营养素 - 始终显示 */}
              <div className="nt-micro-section-label">📌 重点关注</div>
              <div className="nt-micro-grid">
                {[
                  { label: '膳食纤维', value: summary?.total_fiber || 0, target: goal?.target_fiber || 25, unit: 'g', color: '#65a30d', warn: false, goalKey: 'target_fiber' },
                  { label: '糖', value: summary?.total_sugar || 0, target: goal?.target_sugar || 50, unit: 'g', color: '#f43f5e', warn: true, goalKey: 'target_sugar' },
                  { label: '饱和脂肪', value: summary?.total_saturated_fat || 0, target: goal?.target_saturated_fat || 22, unit: 'g', color: '#f97316', warn: true, goalKey: 'target_saturated_fat' },
                  { label: '胆固醇', value: summary?.total_cholesterol || 0, target: goal?.target_cholesterol || 300, unit: 'mg', color: '#eab308', warn: true, goalKey: 'target_cholesterol' },
                  { label: '钠', value: summary?.total_sodium || 0, target: goal?.target_sodium || 2000, unit: 'mg', color: '#a855f7', warn: true, goalKey: 'target_sodium' },
                  { label: '钙', value: summary?.total_calcium || 0, target: goal?.target_calcium || 800, unit: 'mg', color: '#06b6d4', warn: false, goalKey: 'target_calcium' },
                  { label: '铁', value: summary?.total_iron || 0, target: goal?.target_iron || 15, unit: 'mg', color: '#ef4444', warn: false, goalKey: 'target_iron' },
                  { label: '维生素C', value: summary?.total_vitamin_c || 0, target: goal?.target_vitamin_c || 100, unit: 'mg', color: '#f59e0b', warn: false, goalKey: 'target_vitamin_c' },
                ].map((item, idx) => {
                  const pct = item.target > 0 ? Math.min(100, Math.round((item.value / item.target) * 100)) : 0;
                  const isOver = item.warn && pct >= 100;
                  const driInfo = DRI_REFERENCE[item.goalKey];
                  return (
                    <div key={idx} className="nt-micro-item">
                      <div className="nt-micro-row">
                        <span className="nt-micro-label">{item.label}</span>
                        <span className={`nt-micro-val ${isOver ? 'over' : ''}`}>
                          {Math.round(item.value * 10) / 10}{item.unit}
                        </span>
                      </div>
                      <div className="nt-micro-bar-bg">
                        <div className="nt-micro-bar" style={{ width: `${pct}%`, background: isOver ? '#ef4444' : item.color }} />
                      </div>
                      <div className="nt-micro-dri-row">
                        <span className="nt-micro-target">{item.warn ? '上限' : '目标'} {item.target}{item.unit} ({pct}%)</span>
                        {driInfo && <span className="nt-micro-dri">📋 {driInfo.source}: {driInfo.dri}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 矿物质 - 可折叠 */}
              <div className="nt-collapse-header" style={{marginTop: 16}} onClick={() => setExpandMinerals(!expandMinerals)}>
                <span>🪨 更多矿物质</span>
                <span className={`nt-collapse-arrow ${expandMinerals ? 'open' : ''}`}>›</span>
              </div>
              {expandMinerals && (
                <div className="nt-micro-grid nt-collapse-body">
                  {[
                    { label: '钾', value: (summary as any)?.total_potassium || 0, target: goal?.target_potassium || 2000, unit: 'mg', color: '#8b5cf6', goalKey: 'target_potassium' },
                    { label: '锌', value: (summary as any)?.total_zinc || 0, target: goal?.target_zinc || 12, unit: 'mg', color: '#14b8a6', goalKey: 'target_zinc' },
                    { label: '镁', value: (summary as any)?.total_magnesium || 0, target: goal?.target_magnesium || 330, unit: 'mg', color: '#6366f1', goalKey: 'target_magnesium' },
                    { label: '磷', value: (summary as any)?.total_phosphorus || 0, target: goal?.target_phosphorus || 720, unit: 'mg', color: '#0ea5e9', goalKey: 'target_phosphorus' },
                    { label: '硒', value: (summary as any)?.total_selenium || 0, target: goal?.target_selenium || 60, unit: 'μg', color: '#d946ef', goalKey: 'target_selenium' },
                  ].map((item, idx) => {
                    const pct = item.target > 0 ? Math.min(100, Math.round((item.value / item.target) * 100)) : 0;
                    const driInfo = DRI_REFERENCE[item.goalKey];
                    return (
                      <div key={idx} className="nt-micro-item">
                        <div className="nt-micro-row">
                          <span className="nt-micro-label">{item.label}</span>
                          <span className="nt-micro-val">{Math.round(item.value * 10) / 10}{item.unit}</span>
                        </div>
                        <div className="nt-micro-bar-bg">
                          <div className="nt-micro-bar" style={{ width: `${pct}%`, background: item.color }} />
                        </div>
                        <div className="nt-micro-dri-row">
                          <span className="nt-micro-target">目标 {item.target}{item.unit} ({pct}%)</span>
                          {driInfo && <span className="nt-micro-dri">📋 {driInfo.source}: {driInfo.dri}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 维生素 - 可折叠 */}
              <div className="nt-collapse-header" style={{marginTop: 12}} onClick={() => setExpandVitamins(!expandVitamins)}>
                <span>💊 更多维生素</span>
                <span className={`nt-collapse-arrow ${expandVitamins ? 'open' : ''}`}>›</span>
              </div>
              {expandVitamins && (
                <div className="nt-micro-grid nt-collapse-body">
                  {[
                    { label: '维生素A', value: (summary as any)?.total_vitamin_a || 0, target: goal?.target_vitamin_a || 800, unit: 'μg', color: '#f97316', goalKey: 'target_vitamin_a' },
                    { label: '维生素D', value: (summary as any)?.total_vitamin_d || 0, target: goal?.target_vitamin_d || 10, unit: 'μg', color: '#eab308', goalKey: 'target_vitamin_d' },
                    { label: '维生素E', value: (summary as any)?.total_vitamin_e || 0, target: goal?.target_vitamin_e || 14, unit: 'mg', color: '#84cc16', goalKey: 'target_vitamin_e' },
                    { label: '维生素K', value: (summary as any)?.total_vitamin_k || 0, target: goal?.target_vitamin_k || 80, unit: 'μg', color: '#22c55e', goalKey: 'target_vitamin_k' },
                    { label: '维生素B1', value: (summary as any)?.total_vitamin_b1 || 0, target: goal?.target_vitamin_b1 || 1.4, unit: 'mg', color: '#06b6d4', goalKey: 'target_vitamin_b1' },
                    { label: '维生素B2', value: (summary as any)?.total_vitamin_b2 || 0, target: goal?.target_vitamin_b2 || 1.4, unit: 'mg', color: '#0ea5e9', goalKey: 'target_vitamin_b2' },
                    { label: '维生素B6', value: (summary as any)?.total_vitamin_b6 || 0, target: goal?.target_vitamin_b6 || 1.4, unit: 'mg', color: '#3b82f6', goalKey: 'target_vitamin_b6' },
                    { label: '维生素B12', value: (summary as any)?.total_vitamin_b12 || 0, target: goal?.target_vitamin_b12 || 2.4, unit: 'μg', color: '#6366f1', goalKey: 'target_vitamin_b12' },
                    { label: '叶酸', value: (summary as any)?.total_folate || 0, target: goal?.target_folate || 400, unit: 'μg', color: '#8b5cf6', goalKey: 'target_folate' },
                    { label: '烟酸(B3)', value: (summary as any)?.total_niacin || 0, target: goal?.target_niacin || 14, unit: 'mg', color: '#a855f7', goalKey: 'target_niacin' },
                  ].map((item, idx) => {
                    const pct = item.target > 0 ? Math.min(100, Math.round((item.value / item.target) * 100)) : 0;
                    const driInfo = DRI_REFERENCE[item.goalKey];
                    return (
                      <div key={idx} className="nt-micro-item">
                        <div className="nt-micro-row">
                          <span className="nt-micro-label">{item.label}</span>
                          <span className="nt-micro-val">{Math.round(item.value * 100) / 100}{item.unit}</span>
                        </div>
                        <div className="nt-micro-bar-bg">
                          <div className="nt-micro-bar" style={{ width: `${pct}%`, background: item.color }} />
                        </div>
                        <div className="nt-micro-dri-row">
                          <span className="nt-micro-target">目标 {item.target}{item.unit} ({pct}%)</span>
                          {driInfo && <span className="nt-micro-dri">📋 {driInfo.source}: {driInfo.dri}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="nt-dri-note">
                💡 DRI参考来源：中国居民膳食营养素参考摄入量(2023版)、WHO指南
                <br/>RNI=推荐摄入量 AI=适宜摄入量 UL=可耐受最高摄入量
              </div>
            </div>

            {/* Water Tracker */}
            <div className="nt-water-card" ref={waterSectionRef}>
              <div className="nt-water-header">
                <span>💧 饮水</span>
                <span className="nt-water-count">{summary?.water_cups || 0} / {goal?.target_water_cups || 8} 杯</span>
              </div>
              <div className="nt-water-bar-bg">
                <div className="nt-water-bar" style={{ width: `${waterPct}%` }} />
              </div>
              <div className="nt-water-cups">
                {Array.from({ length: goal?.target_water_cups || 8 }).map((_, i) => (
                  <span key={i} className={`nt-water-cup ${i < (summary?.water_cups || 0) ? 'filled' : ''}`}>
                    💧
                  </span>
                ))}
                <button className="nt-water-add" onClick={handleAddWater}>+</button>
              </div>
            </div>

            {/* Meal Records */}
            <div className="nt-meals-section">
              <div className="nt-meals-header">
                <h3 className="nt-section-title">🍽️ 饮食记录</h3>
                <button className="nt-add-btn" onClick={() => setShowAddMeal(true)}>+ 记录</button>
              </div>

              {MEAL_TYPES.map(mt => {
                const mealGroup = grouped[mt.key];
                const meals = mealGroup?.meals || [];
                return (
                  <div key={mt.key} className="nt-meal-group">
                    <div className="nt-meal-group-header">
                      <span className="nt-meal-group-icon">{mt.icon}</span>
                      <span className="nt-meal-group-label">{mt.label}</span>
                      <span className="nt-meal-group-cal">{Math.round(mealGroup?.total_calories || 0)} kcal</span>
                      <button className="nt-meal-group-add" onClick={() => { setAddMealType(mt.key); setShowAddMeal(true); }}>+</button>
                    </div>
                    {meals.length > 0 && (
                      <div className="nt-meal-items">
                        {meals.map(meal => (
                          <div key={meal.id} className="nt-meal-item">
                            <span className="nt-meal-icon">{meal.icon}</span>
                            <div className="nt-meal-info">
                              <span className="nt-meal-name">{meal.food_name}</span>
                              <span className="nt-meal-detail">{meal.amount}g · {Math.round(meal.calories)} kcal</span>
                              <div className="nt-meal-micro">
                                {(() => {
                                  const chips: { k: string; t: string; v: number; u: string; tone: 'warn' | 'ok' | 'info' }[] = [];
                                  const push = (k: string, t: string, v: any, u: string, tone: 'warn' | 'ok' | 'info') => {
                                    const n = Number(v);
                                    if (!Number.isFinite(n) || Math.abs(n) < 1e-6) return;
                                    chips.push({ k, t, v: n, u, tone });
                                  };
                                  push('sodium', '钠', meal.sodium, 'mg', 'warn');
                                  push('potassium', '钾', (meal as any).potassium, 'mg', 'ok');
                                  push('calcium', '钙', meal.calcium, 'mg', 'ok');
                                  push('iron', '铁', meal.iron, 'mg', 'info');
                                  push('vitc', '维C', meal.vitamin_c, 'mg', 'ok');
                                  push('chol', '胆固醇', meal.cholesterol, 'mg', 'warn');
                                  push('zinc', '锌', (meal as any).zinc, 'mg', 'info');
                                  push('mag', '镁', (meal as any).magnesium, 'mg', 'info');
                                  push('sel', '硒', (meal as any).selenium, 'μg', 'info');

                                  // 优先展示：钠/钾/钙/铁/维C/胆固醇，若不足再补
                                  const order = ['sodium', 'potassium', 'calcium', 'iron', 'vitc', 'chol', 'zinc', 'mag', 'sel'];
                                  chips.sort((a, b) => order.indexOf(a.k) - order.indexOf(b.k));
                                  const shown = chips.slice(0, 6);
                                  if (shown.length === 0) {
                                    return <span className="nt-meal-micro-empty">无微量摘要</span>;
                                  }
                                  return shown.map((c) => (
                                    <span key={c.k} className={`nt-micro-chip ${c.tone}`}>
                                      <span className="k">{c.t}</span>
                                      <span className="v">{Math.round(c.v * 10) / 10}{c.u}</span>
                                    </span>
                                  ));
                                })()}
                              </div>

                              <div className="nt-meal-more">
                                <button
                                  type="button"
                                  className="nt-meal-more-btn"
                                  onClick={() => setExpandedMealId((prev) => (prev === meal.id ? null : meal.id))}
                                >
                                  {expandedMealId === meal.id ? '收起营养详情' : '展开营养详情'}
                                  <span className={`nt-meal-more-arrow ${expandedMealId === meal.id ? 'open' : ''}`}>›</span>
                                </button>
                              </div>

                              {expandedMealId === meal.id && (
                                <div className="nt-meal-detail-panel">
                                  <div className="nt-md-grid">
                                    <div className="nt-md-section">
                                      <div className="nt-md-title">宏量</div>
                                      <div className="nt-md-items">
                                        <div className="nt-md-item"><span>蛋白质</span><strong>{Math.round((meal.protein || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>碳水</span><strong>{Math.round((meal.carbs || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>脂肪</span><strong>{Math.round((meal.fat || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>纤维</span><strong>{Math.round((meal.fiber || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>糖</span><strong>{Math.round((meal.sugar || 0) * 10) / 10} g</strong></div>
                                      </div>
                                    </div>

                                    <div className="nt-md-section">
                                      <div className="nt-md-title">脂肪细分</div>
                                      <div className="nt-md-items">
                                        <div className="nt-md-item"><span>饱和脂肪</span><strong>{Math.round((meal.saturated_fat || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>不饱和脂肪</span><strong>{Math.round(((meal as any).unsaturated_fat || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>反式脂肪</span><strong>{Math.round(((meal as any).trans_fat || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>胆固醇</span><strong>{Math.round((meal.cholesterol || 0) * 10) / 10} mg</strong></div>
                                      </div>
                                    </div>

                                    <div className="nt-md-section">
                                      <div className="nt-md-title">矿物质</div>
                                      <div className="nt-md-items">
                                        <div className="nt-md-item"><span>钠</span><strong>{Math.round((meal.sodium || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>钾</span><strong>{Math.round((((meal as any).potassium) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>钙</span><strong>{Math.round((meal.calcium || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>铁</span><strong>{Math.round((meal.iron || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>锌</span><strong>{Math.round((((meal as any).zinc) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>镁</span><strong>{Math.round((((meal as any).magnesium) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>磷</span><strong>{Math.round((((meal as any).phosphorus) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>硒</span><strong>{Math.round((((meal as any).selenium) || 0) * 10) / 10} μg</strong></div>
                                      </div>
                                    </div>

                                    <div className="nt-md-section">
                                      <div className="nt-md-title">维生素</div>
                                      <div className="nt-md-items">
                                        <div className="nt-md-item"><span>维A</span><strong>{Math.round((((meal as any).vitamin_a) || 0) * 10) / 10} μg</strong></div>
                                        <div className="nt-md-item"><span>维B1</span><strong>{Math.round((((meal as any).vitamin_b1) || 0) * 100) / 100} mg</strong></div>
                                        <div className="nt-md-item"><span>维B2</span><strong>{Math.round((((meal as any).vitamin_b2) || 0) * 100) / 100} mg</strong></div>
                                        <div className="nt-md-item"><span>维B6</span><strong>{Math.round((((meal as any).vitamin_b6) || 0) * 100) / 100} mg</strong></div>
                                        <div className="nt-md-item"><span>维B12</span><strong>{Math.round((((meal as any).vitamin_b12) || 0) * 10) / 10} μg</strong></div>
                                        <div className="nt-md-item"><span>维C</span><strong>{Math.round((meal.vitamin_c || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>维D</span><strong>{Math.round((((meal as any).vitamin_d) || 0) * 10) / 10} μg</strong></div>
                                        <div className="nt-md-item"><span>维E</span><strong>{Math.round((((meal as any).vitamin_e) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>维K</span><strong>{Math.round((((meal as any).vitamin_k) || 0) * 10) / 10} μg</strong></div>
                                        <div className="nt-md-item"><span>叶酸</span><strong>{Math.round((((meal as any).folate) || 0) * 10) / 10} μg</strong></div>
                                        <div className="nt-md-item"><span>烟酸</span><strong>{Math.round((((meal as any).niacin) || 0) * 10) / 10} mg</strong></div>
                                      </div>
                                    </div>

                                    <div className="nt-md-section">
                                      <div className="nt-md-title">其他</div>
                                      <div className="nt-md-items">
                                        <div className="nt-md-item"><span>水分</span><strong>{Math.round((((meal as any).water) || 0) * 10) / 10} g</strong></div>
                                        <div className="nt-md-item"><span>咖啡因</span><strong>{Math.round((((meal as any).caffeine) || 0) * 10) / 10} mg</strong></div>
                                        <div className="nt-md-item"><span>酒精</span><strong>{Math.round((((meal as any).alcohol) || 0) * 10) / 10} g</strong></div>
                                      </div>
                                    </div>
                                  </div>

                                  {meal.note && (
                                    <div className="nt-md-note">备注：{meal.note}</div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="nt-meal-actions">
                              <button className="nt-meal-edit" onClick={() => openEditMeal(meal)} aria-label={`编辑${meal.food_name}`}>编辑</button>
                              <button className="nt-meal-del" onClick={() => handleDeleteMeal(meal.id)} aria-label={`删除${meal.food_name}`}>✕</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* Add Meal Modal */}
      {showAddMeal && (
        <div className="nt-modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddMeal(false)}>
          <div className="nt-modal" role="dialog" aria-modal="true" aria-labelledby="nt-add-meal-title">
            <div className="nt-modal-header">
              <h3 id="nt-add-meal-title">{MEAL_TYPES.find(m => m.key === addMealType)?.icon} 添加{MEAL_TYPES.find(m => m.key === addMealType)?.label}</h3>
              <button className="nt-modal-close" onClick={() => setShowAddMeal(false)} aria-label="关闭添加饮食弹窗">✕</button>
            </div>
            <div className="nt-modal-body">
              {/* Meal type selector */}
              <div className="nt-meal-type-bar">
                {MEAL_TYPES.map(mt => (
                  <button key={mt.key} className={`nt-mt-btn ${addMealType === mt.key ? 'active' : ''}`}
                    style={addMealType === mt.key ? { borderColor: mt.color, color: mt.color } : {}}
                    onClick={() => setAddMealType(mt.key)}>
                    {mt.icon} {mt.label}
                  </button>
                ))}
              </div>

              {quickOptions && (
                <section className="nt-quick-options" aria-label="快捷复用饮食">
                  <div className="nt-quick-heading"><strong>快速记录</strong><span>常用食物 3 步内完成</span></div>
                  <div className="nt-quick-actions">
                    {quickOptions.last_meal && (
                      <button type="button" className="nt-repeat-btn" disabled={submitting}
                        onClick={() => handleCopyMealIds(quickOptions.last_meal!.items.map(item => item.id))}>
                        ↻ 重复上一餐
                        <small>{quickOptions.last_meal.items.map(item => item.food_name).join('、')}</small>
                      </button>
                    )}
                    {quickOptions.frequent_combos.slice(0, 2).map(combo => (
                      <button type="button" className="nt-repeat-btn" key={combo.name} disabled={submitting}
                        onClick={() => handleCopyMealIds(combo.items.map(item => item.id))}>
                        ⚡ {combo.name}<small>使用 {combo.uses || 1} 次</small>
                      </button>
                    ))}
                  </div>
                  {(quickOptions.favorite_foods.length > 0 || quickOptions.recent_foods.length > 0) && (
                    <div className="nt-quick-foods">
                      {(quickOptions.favorite_foods.length ? quickOptions.favorite_foods : quickOptions.recent_foods).slice(0, 8).map(food => (
                        <button type="button" key={food.id} onClick={() => { setShowCustomInput(false); selectFoodForCart(food); }}>
                          {food.is_favorite ? '★' : food.icon} {food.name}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* Toggle custom input */}
              <div className="nt-toggle-row">
                <button className={`nt-toggle-btn ${!showCustomInput ? 'active' : ''}`} onClick={() => { setShowCustomInput(false); setCustomFoodName(''); setCustomAliases(''); }}>
                  从食物库选择
                </button>
                <button className={`nt-toggle-btn ${showCustomInput ? 'active' : ''}`} onClick={() => { setShowCustomInput(true); setSelectedFood(null); }}>
                  自定义输入
                </button>
              </div>

              {showCustomInput ? (
                <div className="nt-custom-form">
                  <div className="nt-form-group">
                    <label>食物名称</label>
                    <input type="text" placeholder="例如：红烧排骨" value={customFoodName} onChange={e => setCustomFoodName(e.target.value)} />
                  </div>
                  <div className="nt-form-group">
                    <label>搜索别名或拼音（可选）</label>
                    <input type="text" placeholder="例如：家常排骨 hspg" value={customAliases} onChange={e => setCustomAliases(e.target.value)} />
                  </div>
                  <div className="nt-form-row">
                    <div className="nt-form-group">
                      <label>份量(g)</label>
                      <input type="number" value={mealAmount} onChange={e => setMealAmount(e.target.value)} />
                    </div>
                    <div className="nt-form-group">
                      <label>热量(kcal)</label>
                      <input type="number" placeholder="可选" value={customCalories} onChange={e => setCustomCalories(e.target.value)} />
                    </div>
                  </div>
                  <label className="nt-save-custom">
                    <input type="checkbox" checked={saveCustomFood} onChange={event => setSaveCustomFood(event.target.checked)} />
                    保存为“我的食物”，下次可直接搜索和收藏
                  </label>
                </div>
              ) : (
                <>
                  {/* Search（防抖 320ms，支持多个关键词空格分隔） */}
                  <div className="nt-search-bar">
                    <input
                      ref={searchInputRef}
                      type="text"
                      inputMode="search"
                      enterKeyHint="search"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="搜索名称，如：鸡胸、米饭、奶茶（多个词用空格）"
                      value={foodSearchInput}
                      onChange={e => setFoodSearchInput(e.target.value)}
                    />
                  </div>
                  <div className="nt-search-meta">
                    {foodSearch ? `「${foodSearch}」搜索结果` : '最近使用与推荐'} · {foods.length} 条
                  </div>

                  {/* Category filter */}
                  <div className="nt-cat-bar">
                    {FOOD_CATEGORIES.map(cat => (
                      <button key={cat.key} className={`nt-cat-btn ${foodCategory === cat.key ? 'active' : ''}`}
                        onClick={() => setFoodCategory(cat.key)}>
                        {cat.icon} {cat.label}
                      </button>
                    ))}
                  </div>

                  {/* Food list */}
                  <div className="nt-food-list">
                    {foods.map(food => (
                      <div className="nt-food-row" key={food.id}>
                        <button type="button" aria-pressed={mealCart.some(item => item.food.id === food.id)}
                          className={`nt-food-item ${mealCart.some(item => item.food.id === food.id) ? 'selected' : ''}`}
                          onClick={() => selectFoodForCart(food)}>
                          <span className="nt-food-icon">{food.icon}</span>
                          <div className="nt-food-info">
                            <span className="nt-food-name">{food.name}</span>
                            <span className="nt-food-cal">{food.calories} kcal/100g{food.usage_count ? ` · 用过${food.usage_count}次` : ''}</span>
                          </div>
                          <div className="nt-food-macros">
                            <span className="nt-fm p">蛋白{food.protein}g</span>
                            <span className="nt-fm f">脂肪{food.fat}g</span>
                            <span className="nt-fm c">碳水{food.carbs}g</span>
                          </div>
                        </button>
                        <button type="button" className={`nt-favorite-btn ${food.is_favorite ? 'active' : ''}`}
                          onClick={() => handleToggleFavorite(food)} aria-label={food.is_favorite ? `取消收藏${food.name}` : `收藏${food.name}`}>
                          {food.is_favorite ? '★' : '☆'}
                        </button>
                      </div>
                    ))}
                    {foods.length === 0 && <div className="nt-empty">未找到食物
                      {foodSearch && <button type="button" onClick={() => { setCustomFoodName(foodSearch); setShowCustomInput(true); }}>创建“{foodSearch}”为我的食物</button>}
                    </div>}
                  </div>

                  {/* Amount input */}
                  {selectedFood && (
                    <div className="nt-amount-bar">
                      <span className="nt-amount-label">{selectedFood.icon} {selectedFood.name}</span>
                      <div className="nt-amount-input-wrap">
                        <input type="number" min="0.1" value={mealAmount} onChange={e => { setMealAmount(e.target.value); updateCartAmount(selectedFood.id, Number(e.target.value)); }} className="nt-amount-input" />
                        <span className="nt-amount-unit">g</span>
                      </div>
                      <span className="nt-amount-cal">
                        ≈ {Math.round(selectedFood.calories * (parseFloat(mealAmount) || 0) / 100)} kcal
                      </span>
                      <button type="button" className="nt-cart-remove" onClick={() => removeCartFood(selectedFood.id)} aria-label={`移除${selectedFood.name}`}>✕</button>
                    </div>
                  )}
                  {selectedFood && <div className="nt-portion-shortcuts" aria-label="快捷份量">
                    {[[50, '半份'], [100, '1份'], [150, '大份'], [250, '1碗/杯']].map(([grams, label]) => (
                      <button type="button" key={grams} className={Number(mealAmount) === grams ? 'active' : ''}
                        onClick={() => updateCartAmount(selectedFood.id, Number(grams))}>{label}<small>{grams}g</small></button>
                    ))}
                  </div>}
                </>
              )}

              {mealCart.length > 0 && (
                <div className="nt-meal-cart">
                  <div className="nt-cart-title"><strong>本餐已选 {mealCart.length} 种</strong><span>约 {Math.round(mealCart.reduce((sum, item) => sum + item.food.calories * item.amount / 100, 0))} kcal</span></div>
                  {mealCart.map(item => <div className="nt-cart-item" key={item.food.id}>
                    <span>{item.food.icon} {item.food.name}</span>
                    <input type="number" min="0.1" value={item.amount} onChange={event => updateCartAmount(item.food.id, Number(event.target.value))} aria-label={`${item.food.name}份量`} />
                    <em>g</em>
                    <button type="button" onClick={() => removeCartFood(item.food.id)} aria-label={`移除${item.food.name}`}>✕</button>
                  </div>)}
                </div>
              )}

              <button className="nt-submit-btn" onClick={handleAddMeal}
                disabled={submitting || (mealCart.length === 0 && !customFoodName.trim()) || (customFoodName.trim() !== '' && Number(mealAmount) <= 0)}>
                {submitting ? '添加中...' : `记录本餐${mealCart.length > 1 ? `（${mealCart.length}种食物）` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingMeal && (
        <div className="nt-modal-overlay" onClick={event => event.target === event.currentTarget && setEditingMeal(null)}>
          <div className="nt-modal nt-edit-modal" role="dialog" aria-modal="true" aria-labelledby="nt-edit-meal-title">
            <div className="nt-modal-header">
              <h3 id="nt-edit-meal-title">编辑饮食记录</h3>
              <button className="nt-modal-close" onClick={() => setEditingMeal(null)} aria-label="关闭编辑饮食弹窗">✕</button>
            </div>
            <div className="nt-modal-body">
              <div className="nt-meal-type-bar">
                {MEAL_TYPES.map(mt => (
                  <button type="button" key={mt.key} className={`nt-mt-btn ${editMealType === mt.key ? 'active' : ''}`}
                    style={editMealType === mt.key ? { borderColor: mt.color, color: mt.color } : {}}
                    onClick={() => setEditMealType(mt.key)}>{mt.icon} {mt.label}</button>
                ))}
              </div>
              <div className="nt-form-group">
                <label htmlFor="nt-edit-food-name">食物名称</label>
                <input id="nt-edit-food-name" value={editFoodName} onChange={event => setEditFoodName(event.target.value)} />
              </div>
              <div className="nt-form-row">
                <div className="nt-form-group">
                  <label htmlFor="nt-edit-amount">份量(g)</label>
                  <input id="nt-edit-amount" type="number" min="0.1" value={editAmount} onChange={event => setEditAmount(event.target.value)} />
                </div>
                <div className="nt-form-group">
                  <label htmlFor="nt-edit-calories">热量(kcal)</label>
                  <input id="nt-edit-calories" type="number" min="0" value={editCalories} onChange={event => setEditCalories(event.target.value)} />
                </div>
              </div>
              <div className="nt-form-group">
                <label htmlFor="nt-edit-note">备注</label>
                <textarea id="nt-edit-note" maxLength={200} value={editNote} onChange={event => setEditNote(event.target.value)} placeholder="可选" />
              </div>
              <button className="nt-submit-btn" onClick={handleUpdateMeal}
                disabled={submitting || !editFoodName.trim() || Number(editAmount) <= 0 || Number(editCalories) < 0}>
                {submitting ? '保存中...' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .nt-root {
          min-height: 100vh;
          background-color: #030712;
          background-image: radial-gradient(circle at 50% 0%, #1e1b4b 0%, #030712 40%);
          color: #f8fafc;
          font-family: inherit;
          -webkit-tap-highlight-color: transparent;
        }

        /* Header */
        .nt-header {
          min-height: 72px;
          display: flex; flex-direction: column; justify-content: center;
          padding: calc(12px + var(--safe-area-inset-top, 0px)) 16px 10px;
          position: sticky; top: 0; z-index: 20;
          background: linear-gradient(180deg, rgba(3,7,18,0.95) 0%, rgba(3,7,18,0) 100%);
        }
        .nt-header-top {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
        }
        .nt-header-top h1 { font-size: 17px; font-weight: 800; color: #f1f5f9; margin: 0; text-align: center; flex: 1; }
        .nt-back-btn, .nt-week-btn {
          flex-shrink: 0;
          width: 34px; height: 34px; border-radius: 50%;
          border: 1px solid rgba(148,163,184,0.15);
          background: rgba(15,23,42,0.6); color: #cbd5e1;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; -webkit-tap-highlight-color: transparent;
        }
        .nt-week-btn { width: auto; min-width: 34px; padding: 0 9px; gap: 4px; font-size: 12px; font-weight: 700; }
        .nt-back-btn:active, .nt-week-btn:active { transform: scale(0.9); }

        .nt-view-tabs {
          display: grid; grid-template-columns: 1fr 1fr; gap: 4px;
          max-width: 608px; margin: 0 auto 8px; padding: 4px;
          background: rgba(15,23,42,0.72); border: 1px solid rgba(148,163,184,0.12); border-radius: 12px;
        }
        .nt-view-tabs button { border: 0; border-radius: 8px; padding: 9px; background: transparent; color: rgba(148,163,184,.75); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
        .nt-view-tabs button.active { color: #a7f3d0; background: rgba(16,185,129,.16); box-shadow: inset 0 0 0 1px rgba(16,185,129,.2); }

        /* Date toolbar */
        .nt-date-toolbar {
          display: flex; align-items: center; justify-content: center; gap: 8px; flex-wrap: wrap;
          min-height: 46px; margin: 0 auto; padding: 0 16px;
          max-width: 640px; box-sizing: border-box;
          border-bottom: 1px solid rgba(148,163,184,0.1);
        }
        .nt-date-arrow {
          width: 28px; height: 28px; border-radius: 50%;
          border: 1px solid rgba(148,163,184,0.2); background: transparent;
          color: rgba(148,163,184,0.8); font-size: 18px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .nt-date-arrow:disabled { opacity: 0.3; cursor: default; }
        .nt-date-arrow:active:not(:disabled) { transform: scale(0.9); }
        .nt-date-text { font-size: 14px; font-weight: 600; color: rgba(226,232,240,0.9); min-width: 100px; text-align: center; }
        .nt-date-picker { position: relative; min-width: 112px; text-align: center; cursor: pointer; }
        .nt-date-picker input { position: absolute; inset: 0; width: 100%; opacity: 0; cursor: pointer; }
        .nt-date-shortcut { border: 1px solid rgba(148,163,184,.18); border-radius: 999px; background: rgba(15,23,42,.6); color: #cbd5e1; padding: 5px 9px; font: inherit; font-size: 10px; cursor: pointer; }
        .nt-hp-feedback { position: sticky; top: 68px; z-index: 19; max-width: 560px; margin: 8px auto 0; padding: 10px 14px; border: 1px solid rgba(52,211,153,.28); border-radius: 12px; background: rgba(6,78,59,.94); color: #d1fae5; text-align: center; font-size: 12px; font-weight: 800; box-shadow: 0 8px 30px rgba(0,0,0,.25); }

        /* Main */
        .nt-main { padding: 16px 16px 100px; }
        .nt-loading { display: flex; flex-direction: column; align-items: center; padding: 80px 20px; gap: 16px; }
        .nt-spinner { width: 32px; height: 32px; border: 3px solid rgba(51,65,85,0.5); border-top-color: #10b981; border-radius: 50%; animation: ntSpin 0.8s linear infinite; }
        @keyframes ntSpin { to { transform: rotate(360deg); } }
        .nt-loading p { color: rgba(148,163,184,0.6); font-size: 13px; }

        /* Calorie Card */
        .nt-cal-card {
          display: flex; align-items: center; gap: 20px;
          padding: 20px; margin-bottom: 16px;
          background: rgba(17,24,39,0.7); backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
        }
        .nt-cal-ring-wrap { position: relative; width: 120px; height: 120px; flex-shrink: 0; }
        .nt-cal-ring-text {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
        }
        .nt-cal-num { font-size: 24px; font-weight: 800; color: white; line-height: 1; }
        .nt-cal-unit { font-size: 11px; color: rgba(148,163,184,0.6); margin-top: 2px; }
        .nt-cal-info { flex: 1; }
        .nt-cal-target { font-size: 13px; color: rgba(148,163,184,0.7); margin-bottom: 4px; }
        .nt-cal-remain { font-size: 14px; font-weight: 600; color: #10b981; }

        /* Macros */
        .nt-macros {
          display: flex; flex-direction: column; gap: 10px;
          padding: 16px; margin-bottom: 16px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
        }
        .nt-macro-header { display: flex; justify-content: space-between; align-items: center; }
        .nt-macro-label { font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.8); }
        .nt-macro-val { font-size: 12px; color: rgba(148,163,184,0.6); }
        .nt-macro-bar-bg { height: 6px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; margin-top: 4px; }
        .nt-macro-bar { height: 100%; border-radius: 3px; transition: width 0.5s ease; }

        /* Micronutrients */
        .nt-micro-card {
          padding: 16px; margin-bottom: 16px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
        }
        .nt-micro-title { font-size: 14px; font-weight: 700; color: rgba(226,232,240,0.9); margin: 0 0 12px 0; }
        .nt-micro-grid { display: flex; flex-direction: column; gap: 10px; }
        .nt-micro-item { }
        .nt-micro-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 3px; }
        .nt-micro-label { font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.7); }
        .nt-micro-val { font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.85); }
        .nt-micro-val.over { color: #ef4444; }
        .nt-micro-bar-bg { height: 4px; background: rgba(30,41,59,0.8); border-radius: 2px; overflow: hidden; }
        .nt-micro-bar { height: 100%; border-radius: 2px; transition: width 0.5s ease; }
        .nt-micro-target { font-size: 10px; color: rgba(148,163,184,0.4); margin-top: 2px; }
        .nt-micro-section-label {
          font-size: 12px; font-weight: 700; color: rgba(226,232,240,0.6);
          margin-bottom: 8px; padding-bottom: 4px;
          border-bottom: 1px solid rgba(51,65,85,0.3);
        }
        .nt-micro-dri-row {
          display: flex; justify-content: space-between; align-items: center;
          margin-top: 2px; gap: 8px;
        }
        .nt-micro-dri {
          font-size: 9px; color: rgba(148,163,184,0.35);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          max-width: 55%;
        }
        .nt-dri-note {
          margin-top: 16px; padding: 10px 12px;
          background: rgba(30,41,59,0.5); border-radius: 8px;
          font-size: 10px; color: rgba(148,163,184,0.4); line-height: 1.6;
        }

        /* Collapsible sections */
        .nt-collapse-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 0; cursor: pointer; user-select: none;
          border-bottom: 1px solid rgba(51,65,85,0.3);
          font-size: 12px; font-weight: 700; color: rgba(148,163,184,0.5);
        }
        .nt-collapse-header:active { opacity: 0.7; }
        .nt-collapse-arrow {
          font-size: 16px; font-weight: 700; transition: transform 0.25s ease;
          display: inline-block;
        }
        .nt-collapse-arrow.open { transform: rotate(90deg); }
        .nt-collapse-body {
          padding-top: 10px;
          animation: ntCollapseIn 0.2s ease;
        }
        @keyframes ntCollapseIn { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 600px; } }

        /* Water */
        .nt-water-card {
          padding: 16px; margin-bottom: 16px;
          background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
        }
        .nt-water-header { display: flex; justify-content: space-between; align-items: center; font-size: 14px; font-weight: 600; color: rgba(226,232,240,0.9); margin-bottom: 8px; }
        .nt-water-count { font-size: 12px; color: rgba(148,163,184,0.6); }
        .nt-water-bar-bg { height: 6px; background: rgba(30,41,59,0.8); border-radius: 3px; overflow: hidden; margin-bottom: 10px; }
        .nt-water-bar { height: 100%; background: #38bdf8; border-radius: 3px; transition: width 0.5s ease; }
        .nt-water-cups { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
        .nt-water-cup { font-size: 18px; opacity: 0.2; transition: opacity 0.3s; }
        .nt-water-cup.filled { opacity: 1; }
        .nt-water-add {
          width: 28px; height: 28px; border-radius: 50%;
          border: 1px dashed rgba(56,189,248,0.5); background: rgba(56,189,248,0.1);
          color: #38bdf8; font-size: 16px; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          margin-left: 4px;
        }
        .nt-water-add:active { transform: scale(0.9); }

        /* Meals Section */
        .nt-meals-section { margin-top: 8px; }
        .nt-meals-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .nt-section-title { font-size: 16px; font-weight: 700; color: rgba(226,232,240,0.95); margin: 0; }
        .nt-add-btn {
          padding: 6px 14px; border: 1px solid rgba(16,185,129,0.4);
          border-radius: 8px; background: rgba(16,185,129,0.1);
          color: #10b981; font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .nt-add-btn:active { transform: scale(0.95); }

        /* Meal Group */
        .nt-meal-group {
          margin-bottom: 10px;
          background: rgba(17,24,39,0.5); border: 1px solid rgba(255,255,255,0.04);
          border-radius: 14px; overflow: hidden;
        }
        .nt-meal-group-header {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 14px;
        }
        .nt-meal-group-icon { font-size: 18px; }
        .nt-meal-group-label { font-size: 14px; font-weight: 600; color: rgba(226,232,240,0.9); flex: 1; }
        .nt-meal-group-cal { font-size: 12px; color: rgba(148,163,184,0.5); }
        .nt-meal-group-add {
          width: 24px; height: 24px; border-radius: 6px;
          border: 1px solid rgba(148,163,184,0.2); background: transparent;
          color: rgba(148,163,184,0.6); font-size: 14px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .nt-meal-group-add:active { transform: scale(0.9); }

        .nt-meal-items { border-top: 1px solid rgba(51,65,85,0.3); }
        .nt-meal-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px;
          border-bottom: 1px solid rgba(51,65,85,0.15);
        }
        .nt-meal-item:last-child { border-bottom: none; }
        .nt-meal-icon { font-size: 20px; flex-shrink: 0; }
        .nt-meal-info { flex: 1; min-width: 0; }
        .nt-meal-name { display: block; font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); }
        .nt-meal-detail { display: block; font-size: 11px; color: rgba(148,163,184,0.5); margin-top: 2px; }
        .nt-meal-micro {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }
        .nt-meal-micro-empty {
          font-size: 11px;
          color: rgba(148,163,184,0.45);
        }
        .nt-micro-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 8px;
          border-radius: 999px;
          border: 1px solid rgba(148,163,184,0.14);
          background: rgba(2,6,23,0.35);
          font-size: 11px;
          line-height: 1;
          white-space: nowrap;
        }
        .nt-micro-chip .k { color: rgba(148,163,184,0.7); font-weight: 800; }
        .nt-micro-chip .v { color: rgba(226,232,240,0.92); font-weight: 900; }
        .nt-micro-chip.warn { border-color: rgba(244,63,94,0.22); background: rgba(244,63,94,0.08); }
        .nt-micro-chip.warn .k, .nt-micro-chip.warn .v { color: rgba(252,165,165,0.95); }
        .nt-micro-chip.ok { border-color: rgba(16,185,129,0.22); background: rgba(16,185,129,0.08); }
        .nt-micro-chip.ok .v { color: rgba(110,231,183,0.95); }
        .nt-micro-chip.info { border-color: rgba(56,189,248,0.22); background: rgba(56,189,248,0.06); }
        .nt-micro-chip.info .v { color: rgba(125,211,252,0.95); }

        .nt-meal-more { margin-top: 8px; }
        .nt-meal-more-btn {
          background: transparent;
          border: 1px solid rgba(51,65,85,0.35);
          color: rgba(148,163,184,0.85);
          padding: 6px 10px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: inherit;
        }
        .nt-meal-more-btn:active { transform: scale(0.98); }
        .nt-meal-more-arrow { display: inline-block; transition: transform 0.2s ease; }
        .nt-meal-more-arrow.open { transform: rotate(90deg); }

        .nt-meal-detail-panel {
          margin-top: 10px;
          padding: 12px;
          border-radius: 14px;
          border: 1px solid rgba(51,65,85,0.25);
          background: rgba(2,6,23,0.25);
        }
        .nt-md-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 10px;
        }
        @media (min-width: 520px) {
          .nt-md-grid { grid-template-columns: 1fr 1fr; }
        }
        .nt-md-section {
          border: 1px solid rgba(148,163,184,0.10);
          background: rgba(15,23,42,0.45);
          border-radius: 12px;
          padding: 10px;
        }
        .nt-md-title {
          font-size: 12px;
          font-weight: 900;
          color: rgba(226,232,240,0.9);
          margin-bottom: 8px;
        }
        .nt-md-items { display: grid; grid-template-columns: 1fr; gap: 6px; }
        .nt-md-item {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 10px;
          background: rgba(2,6,23,0.22);
          border: 1px solid rgba(148,163,184,0.06);
          font-size: 12px;
        }
        .nt-md-item span { color: rgba(148,163,184,0.8); font-weight: 700; }
        .nt-md-item strong { color: rgba(226,232,240,0.95); font-weight: 900; }
        .nt-md-note {
          margin-top: 10px;
          font-size: 12px;
          color: rgba(148,163,184,0.75);
          padding: 8px 10px;
          border-radius: 10px;
          border: 1px dashed rgba(148,163,184,0.14);
          background: rgba(15,23,42,0.35);
        }
        .nt-meal-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
        .nt-meal-edit { border: 1px solid rgba(96,165,250,.25); border-radius: 6px; background: rgba(59,130,246,.1); color: #93c5fd; font: inherit; font-size: 10px; font-weight: 700; padding: 4px 7px; cursor: pointer; }
        .nt-meal-del {
          width: 24px; height: 24px; border: none; background: rgba(239,68,68,0.1);
          border-radius: 6px; color: #ef4444; font-size: 12px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
        }
        .nt-meal-del:active { transform: scale(0.9); }

        /* Unified nutrition review */
        .nt-review { padding: 0 4px; }
        .nt-review-heading { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; margin: 2px 0 14px; }
        .nt-review-heading p { margin: 5px 0 0; color: rgba(148,163,184,0.65); font-size: 12px; line-height: 1.5; }
        .nt-review-range { flex-shrink: 0; padding: 5px 8px; border: 1px solid rgba(148,163,184,0.16); border-radius: 8px; color: rgba(203,213,225,0.7); font-size: 10px; }
        .nt-review-tabs { display: grid; grid-template-columns: repeat(3, 1fr); padding: 4px; background: rgba(15,23,42,0.72); border: 1px solid rgba(148,163,184,0.12); border-radius: 12px; gap: 4px; margin-bottom: 14px; }
        .nt-review-tabs button { border: 0; border-radius: 8px; padding: 8px 4px; background: transparent; color: rgba(148,163,184,0.7); font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; }
        .nt-review-tabs button.active { background: rgba(16,185,129,0.16); color: #6ee7b7; box-shadow: inset 0 0 0 1px rgba(16,185,129,0.22); }
        .nt-review-hero { display: flex; align-items: baseline; gap: 6px; padding: 16px; background: linear-gradient(125deg, rgba(16,185,129,0.18), rgba(15,23,42,0.72)); border: 1px solid rgba(52,211,153,0.2); border-radius: 16px; margin-bottom: 10px; }
        .nt-review-hero span { font-size: 13px; color: rgba(226,232,240,0.75); }
        .nt-review-hero strong { color: #a7f3d0; font-size: 30px; line-height: 1; }
        .nt-review-hero small { color: rgba(167,243,208,0.72); font-size: 12px; }
        .nt-review-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }
        .nt-review-stats > div { padding: 11px; background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; display: flex; flex-direction: column; gap: 4px; }
        .nt-review-stats span { color: rgba(148,163,184,0.58); font-size: 10px; }
        .nt-review-stats strong { color: rgba(226,232,240,0.95); font-size: 15px; }
        .nt-review-card { padding: 14px; margin: 10px 0; background: rgba(17,24,39,0.7); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; }
        .nt-review-card-title { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
        .nt-review-card-title h4 { margin: 0; color: rgba(241,245,249,0.95); font-size: 14px; }
        .nt-review-card-title > span { color: rgba(148,163,184,0.55); font-size: 10px; }
        .nt-review-chart { height: 152px; display: flex; align-items: flex-end; gap: 3px; overflow-x: auto; padding: 6px 0 0; }
        .nt-review-bar-wrap { height: 100%; min-width: 28px; flex: 1; border: 0; background: transparent; color: inherit; padding: 0; display: flex; flex-direction: column; align-items: center; gap: 4px; cursor: pointer; }
        .nt-review-bar-wrap > span { height: 14px; color: rgba(148,163,184,0.65); font-size: 9px; }
        .nt-review-bar-wrap i { height: 100px; width: 100%; max-width: 20px; display: flex; align-items: flex-end; background: rgba(30,41,59,0.65); border-radius: 4px; overflow: hidden; }
        .nt-review-bar-wrap i b { display: block; width: 100%; min-height: 4px; background: linear-gradient(to top, #10b981, #6ee7b7); border-radius: 4px; transition: height .4s ease; }
        .nt-review-bar-wrap em { color: rgba(148,163,184,0.65); font-size: 9px; font-style: normal; white-space: nowrap; }
        .nt-review-hint { margin: 10px 0 0; color: rgba(148,163,184,0.48); font-size: 10px; text-align: center; }
        .nt-review-foods { display: flex; flex-direction: column; gap: 8px; }
        .nt-review-foods > div { display: grid; grid-template-columns: 26px minmax(0, 1fr) auto; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid rgba(51,65,85,0.4); }
        .nt-review-foods > div:last-child { border-bottom: 0; padding-bottom: 0; }
        .nt-review-foods > div > span { font-size: 18px; }
        .nt-review-foods b { color: rgba(226,232,240,0.9); font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .nt-review-foods small { color: rgba(148,163,184,0.62); font-size: 10px; text-align: right; }
        .nt-review-meals { display: flex; flex-direction: column; }
        .nt-history-filters { display: grid; grid-template-columns: minmax(0, 1fr) 112px; gap: 8px; margin-bottom: 8px; }
        .nt-history-filters input, .nt-history-filters select { min-width: 0; padding: 9px 10px; border: 1px solid rgba(148,163,184,.18); border-radius: 9px; background: rgba(15,23,42,.72); color: #e2e8f0; font: inherit; font-size: 11px; }
        .nt-history-meal-row { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; align-items: center; gap: 6px; border-bottom: 1px solid rgba(51,65,85,0.4); }
        .nt-history-meal-row:last-child { border-bottom: 0; }
        .nt-history-open { border: 0; background: transparent; color: inherit; padding: 10px 0; display: grid; grid-template-columns: 28px minmax(0, 1fr) auto; align-items: center; gap: 8px; text-align: left; font: inherit; cursor: pointer; }
        .nt-history-edit, .nt-history-copy { border: 1px solid rgba(96,165,250,.2); border-radius: 7px; background: rgba(59,130,246,.08); color: #93c5fd; padding: 5px 7px; font: inherit; font-size: 9px; font-weight: 800; cursor: pointer; white-space: nowrap; }
        .nt-history-copy { border-color: rgba(52,211,153,.2); background: rgba(16,185,129,.08); color: #6ee7b7; }
        .nt-review-meal-icon { font-size: 18px; }
        .nt-review-meal-name { min-width: 0; color: rgba(226,232,240,0.92); font-size: 13px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .nt-review-meal-name small, .nt-review-meals strong small { display: block; color: rgba(148,163,184,0.6); font-size: 10px; font-weight: 400; margin-top: 3px; }
        .nt-review-meals strong { color: #a7f3d0; font-size: 12px; text-align: right; }
        .nt-review-empty { margin: 0; padding: 10px 0 2px; color: rgba(148,163,184,0.62); font-size: 12px; text-align: center; }

        /* Modal */
        .nt-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.75);
          backdrop-filter: blur(8px); display: flex; align-items: center;
          justify-content: center; z-index: 9999; padding: 20px;
          animation: ntFadeIn 200ms;
        }
        @keyframes ntFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .nt-modal {
          background: #111827; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 20px; width: 100%; max-width: 480px;
          max-height: 90vh; display: flex; flex-direction: column;
          animation: ntScaleIn 300ms cubic-bezier(0.34,1.56,0.64,1);
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }
        @keyframes ntScaleIn { from { transform: scale(0.95); opacity: 0.6; } to { transform: scale(1); opacity: 1; } }
        .nt-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid rgba(51,65,85,0.3);
          flex-shrink: 0;
        }
        .nt-modal-header h3 { margin: 0; font-size: 18px; font-weight: 700; }
        .nt-modal-close {
          width: 32px; height: 32px; border: none; border-radius: 50%;
          background: rgba(51,65,85,0.5); color: rgba(148,163,184,0.8);
          font-size: 16px; cursor: pointer; display: flex; align-items: center; justify-content: center;
        }
        .nt-modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }

        .nt-quick-options { margin-bottom: 12px; padding: 11px; border: 1px solid rgba(52,211,153,.16); border-radius: 12px; background: rgba(6,78,59,.12); }
        .nt-quick-heading { display: flex; justify-content: space-between; gap: 8px; align-items: center; margin-bottom: 8px; }
        .nt-quick-heading strong { font-size: 12px; color: #a7f3d0; }
        .nt-quick-heading span { font-size: 9px; color: rgba(148,163,184,.6); }
        .nt-quick-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
        .nt-repeat-btn { min-width: 0; border: 1px solid rgba(52,211,153,.18); border-radius: 9px; background: rgba(16,185,129,.08); color: #d1fae5; padding: 8px; text-align: left; font: inherit; font-size: 11px; font-weight: 800; cursor: pointer; overflow: hidden; }
        .nt-repeat-btn small { display: block; margin-top: 3px; color: rgba(167,243,208,.58); font-size: 8px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .nt-quick-foods { display: flex; gap: 5px; overflow-x: auto; margin-top: 8px; padding-bottom: 2px; }
        .nt-quick-foods button { flex-shrink: 0; border: 1px solid rgba(148,163,184,.16); border-radius: 999px; background: rgba(15,23,42,.72); color: #cbd5e1; padding: 6px 9px; font: inherit; font-size: 10px; cursor: pointer; }

        /* Meal Type Bar */
        .nt-meal-type-bar { display: flex; gap: 6px; margin-bottom: 12px; }
        .nt-mt-btn {
          flex: 1; padding: 8px 4px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 8px; background: transparent;
          color: rgba(148,163,184,0.6); font-size: 11px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .nt-mt-btn.active { background: rgba(16,185,129,0.1); }

        /* Toggle */
        .nt-toggle-row { display: flex; gap: 6px; margin-bottom: 12px; }
        .nt-toggle-btn {
          flex: 1; padding: 8px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 8px; background: transparent;
          color: rgba(148,163,184,0.5); font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit;
        }
        .nt-toggle-btn.active { background: rgba(139,92,246,0.15); color: #a78bfa; border-color: rgba(139,92,246,0.3); }

        /* Search */
        .nt-search-bar { margin-bottom: 6px; }
        .nt-search-meta {
          font-size: 11px; color: rgba(148,163,184,0.8); margin-bottom: 10px;
        }
        .nt-search-bar input {
          width: 100%; padding: 10px 14px; box-sizing: border-box;
          border: 1px solid rgba(51,65,85,0.5); border-radius: 10px;
          background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit;
        }
        .nt-search-bar input:focus { outline: none; border-color: rgba(16,185,129,0.5); }

        /* Category Bar */
        .nt-cat-bar {
          display: flex; gap: 4px; overflow-x: auto; padding-bottom: 8px;
          margin-bottom: 10px; -webkit-overflow-scrolling: touch;
        }
        .nt-cat-bar::-webkit-scrollbar { display: none; }
        .nt-cat-btn {
          padding: 5px 10px; border: 1px solid rgba(51,65,85,0.4);
          border-radius: 6px; background: transparent;
          color: rgba(148,163,184,0.5); font-size: 11px; font-weight: 600;
          cursor: pointer; white-space: nowrap; font-family: inherit;
        }
        .nt-cat-btn.active { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.3); }

        /* Food List */
        .nt-food-list { max-height: 240px; overflow-y: auto; margin-bottom: 12px; }
        .nt-food-row { position: relative; display: flex; align-items: stretch; gap: 4px; margin-bottom: 4px; }
        .nt-food-item {
          display: flex; align-items: center; gap: 10px;
          width: 100%; color: inherit; text-align: left; font: inherit;
          padding: 10px 12px; margin-bottom: 0;
          background: rgba(15,23,42,0.5); border: 1px solid rgba(51,65,85,0.3);
          border-radius: 10px; cursor: pointer; transition: all 0.15s;
        }
        .nt-food-item:active { transform: scale(0.98); }
        .nt-food-item.selected { border-color: rgba(16,185,129,0.5); background: rgba(16,185,129,0.08); }
        .nt-favorite-btn { width: 38px; flex-shrink: 0; border: 1px solid rgba(148,163,184,.16); border-radius: 10px; background: rgba(15,23,42,.5); color: #64748b; font-size: 18px; cursor: pointer; }
        .nt-favorite-btn.active { color: #fbbf24; border-color: rgba(251,191,36,.28); background: rgba(245,158,11,.08); }
        .nt-food-icon { font-size: 24px; flex-shrink: 0; }
        .nt-food-info { flex: 1; min-width: 0; }
        .nt-food-name { display: block; font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); }
        .nt-food-cal { display: block; font-size: 11px; color: rgba(148,163,184,0.5); margin-top: 1px; }
        .nt-food-macros { display: flex; gap: 4px; flex-shrink: 0; }
        .nt-fm { font-size: 9px; padding: 2px 5px; border-radius: 4px; font-weight: 600; }
        .nt-fm.p { background: rgba(59,130,246,0.15); color: #60a5fa; }
        .nt-fm.f { background: rgba(245,158,11,0.15); color: #fbbf24; }
        .nt-fm.c { background: rgba(16,185,129,0.15); color: #34d399; }
        .nt-empty { text-align: center; padding: 20px; color: rgba(148,163,184,0.4); font-size: 13px; }
        .nt-empty button { display: block; margin: 10px auto 0; border: 1px solid rgba(16,185,129,.3); border-radius: 8px; background: rgba(16,185,129,.1); color: #6ee7b7; padding: 8px 10px; font: inherit; font-size: 11px; cursor: pointer; }

        /* Amount Bar */
        .nt-amount-bar {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; margin-bottom: 12px;
          background: rgba(16,185,129,0.08); border: 1px solid rgba(16,185,129,0.2);
          border-radius: 10px;
        }
        .nt-amount-label { font-size: 13px; font-weight: 600; color: rgba(226,232,240,0.9); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .nt-amount-input-wrap { display: flex; align-items: center; gap: 4px; }
        .nt-amount-input {
          width: 60px; padding: 6px 8px; border: 1px solid rgba(51,65,85,0.5);
          border-radius: 6px; background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit; text-align: center;
        }
        .nt-amount-input:focus { outline: none; border-color: rgba(16,185,129,0.5); }
        .nt-amount-unit { font-size: 12px; color: rgba(148,163,184,0.5); }
        .nt-amount-cal { font-size: 12px; font-weight: 600; color: #10b981; white-space: nowrap; }
        .nt-cart-remove { border: 0; background: transparent; color: #f87171; cursor: pointer; }
        .nt-portion-shortcuts { display: grid; grid-template-columns: repeat(4, 1fr); gap: 5px; margin: -5px 0 12px; }
        .nt-portion-shortcuts button { border: 1px solid rgba(148,163,184,.16); border-radius: 8px; background: rgba(15,23,42,.55); color: #cbd5e1; padding: 6px 3px; font: inherit; font-size: 10px; cursor: pointer; }
        .nt-portion-shortcuts button.active { border-color: rgba(52,211,153,.38); color: #6ee7b7; background: rgba(16,185,129,.1); }
        .nt-portion-shortcuts small { display: block; margin-top: 2px; font-size: 8px; color: rgba(148,163,184,.55); }
        .nt-meal-cart { margin: 10px 0 12px; padding: 10px; border: 1px solid rgba(139,92,246,.2); border-radius: 11px; background: rgba(76,29,149,.08); }
        .nt-cart-title { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 7px; font-size: 11px; }
        .nt-cart-title strong { color: #ddd6fe; } .nt-cart-title span { color: #a78bfa; }
        .nt-cart-item { display: grid; grid-template-columns: minmax(0, 1fr) 56px 12px 24px; gap: 4px; align-items: center; padding: 5px 0; font-size: 11px; color: #cbd5e1; }
        .nt-cart-item input { width: 56px; box-sizing: border-box; border: 1px solid rgba(148,163,184,.18); border-radius: 6px; background: rgba(15,23,42,.72); color: #f8fafc; padding: 5px; text-align: center; }
        .nt-cart-item em { color: #64748b; font-style: normal; }
        .nt-cart-item button { border: 0; background: transparent; color: #f87171; cursor: pointer; }

        /* Custom Form */
        .nt-custom-form { margin-bottom: 12px; }
        .nt-form-group { margin-bottom: 12px; }
        .nt-form-group label { display: block; font-size: 12px; font-weight: 600; color: rgba(226,232,240,0.7); margin-bottom: 4px; }
        .nt-form-group input, .nt-form-group textarea {
          width: 100%; padding: 10px 12px; box-sizing: border-box;
          border: 1px solid rgba(51,65,85,0.5); border-radius: 8px;
          background: rgba(15,23,42,0.8); color: #f8fafc;
          font-size: 14px; font-family: inherit;
        }
        .nt-form-group textarea { min-height: 76px; resize: vertical; }
        .nt-form-group input:focus, .nt-form-group textarea:focus { outline: none; border-color: rgba(16,185,129,0.5); }
        .nt-form-row { display: flex; gap: 10px; }
        .nt-form-row .nt-form-group { flex: 1; }
        .nt-save-custom { display: flex; align-items: center; gap: 7px; color: rgba(203,213,225,.72); font-size: 11px; cursor: pointer; }

        /* Submit */
        .nt-submit-btn {
          width: 100%; padding: 14px; border: none; border-radius: 12px;
          background: #10b981; color: white;
          font-size: 15px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
          margin-bottom: env(safe-area-inset-bottom, 16px);
        }
        .nt-submit-btn:disabled { opacity: 0.5; }
        .nt-submit-btn:active:not(:disabled) { transform: scale(0.98); }

        @media (min-width: 768px) {
          .nt-main { max-width: 640px; margin-left: auto; margin-right: auto; }
        }
        @media (max-width: 480px) {
          .nt-modal-overlay { padding: 8px; align-items: flex-end; }
          .nt-modal { max-height: 94vh; border-radius: 18px 18px 10px 10px; }
          .nt-modal-body { padding: 14px; }
          .nt-food-macros { display: none; }
          .nt-history-meal-row { grid-template-columns: minmax(0, 1fr) auto; }
          .nt-history-copy { grid-column: 1 / -1; margin: -4px 0 8px 36px; }
        }
      `}</style>
    </div>
  );
};

export default NutritionManager;
